/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import {
    useLocale,
    useCalendar,
    type CalendarProps as AriaCalendarProps,
} from 'react-aria';
import { useCalendarState } from 'react-stately';
import type { DateValue } from '@internationalized/date';

import { CalendarFrame, type CalendarFrameDisplayProps } from './CalendarFrame';
import { createCalendar } from './createCalendar';

export interface SingleDateCalendarProps<T extends DateValue>
    extends AriaCalendarProps<T>, CalendarFrameDisplayProps {}

function SingleDateCalendarBase<T extends DateValue>({
    numVisibleMonths = 1,
    headingLevel = 2,
    pageBehavior = 'single',
    size = 'default',
    layout = 'paged',
    initialScrollDate,
    ...props
}: SingleDateCalendarProps<T>) {
    // Only the stacked initial scroll uses this; unlike the range hook,
    // `useCalendar` takes no ref.
    const ref = React.useRef<HTMLDivElement>(null);
    const { locale } = useLocale();
    const state = useCalendarState({
        ...props,
        pageBehavior,
        visibleDuration: { months: numVisibleMonths },
        locale,
        createCalendar,
    });

    const aria = useCalendar({ ...props, pageBehavior }, state);

    return (
        <CalendarFrame
            state={state}
            aria={aria}
            calendarRef={ref}
            numVisibleMonths={numVisibleMonths}
            headingLevel={headingLevel}
            size={size}
            layout={layout}
            initialScrollDate={initialScrollDate}
        />
    );
}

/**
 * Memoized because the stacked layout renders hundreds of cells. Callers need
 * stable props (memoized `value`, stable callbacks) for this to skip renders.
 * The cast keeps the generic signature, which React.memo would otherwise drop.
 */
export const SingleDateCalendar = React.memo(
    SingleDateCalendarBase
) as typeof SingleDateCalendarBase;
